import { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useRouter } from 'next/router'

import DeckService from 'services/deck.service'

import { store } from 'app-redux/store'
import { useActionless, useAppSelector } from 'app-redux/hooks'
import { SET_DECK_STATE } from 'app-redux/deck/actions/types'

import Modal from 'components/elements/Modal'
import PhatButton from 'components/formElements/PhatButton'
import Link from 'components/elements/Link'

import { deckResponseToDeckState } from 'utils/responseToDeck'

import styles from './xxlDeckLoaderModal.module.scss'
import ToastService from 'services/toast.service'

const XXLDeckLoaderModal = () => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const router = useRouter()

  const ownerId = useAppSelector(state => state.deck.ownerid)
  const intentionallySkippedCardData = useAppSelector(state => state.deck.intentionallySkippedCardData)

  const [{ tbId: userId, settings = {} }] = useCookies(['tbId', 'settings'])

  const [setDeckState] = useActionless(SET_DECK_STATE)

  const isOwner = `${ownerId}` === `${userId}`

  const handleLoadCards = () => {
    const deckId = router.query.id || (router.query.deckInfo && router.query.deckInfo[0]) || null

    if (!deckId) return ToastService.create('Unable to find deck ID', 'Deck Service', 'error')

    setLoading(true)

    return DeckService.details(`${deckId}`)
      .then(deck => {
        const showCustomUploads = (settings && settings['show_custom_uploads']) || false
        const deckState = store.getState().deck
        const mergedDeckStates = deckResponseToDeckState(deckState, deck, false, userId, showCustomUploads)

        setOpen(false)
        setDeckState({ cardMap: mergedDeckStates.cardMap, intentionallySkippedCardData: false })
      })
      .catch(() =>
        ToastService.create(
          'An error occurred while loading cards. If this problem continues, please contact support.',
          'Card service',
          'error',
        ),
      )
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!intentionallySkippedCardData) return

    setTimeout(() => setOpen(true), 300)
  }, [])

  return (
    <Modal open={open} modalClassName={styles.container} noWeirdClose closeButton>
      <h2>Whoa, that's a lot of cards</h2>

      <p>
        This deck has too many unique cards to render on initial load. You can still load the deck using the button
        below, but we'll be outsourcing the heavy lifting from our servers to your browser.
      </p>

      {isOwner && (
        <p>
          Our <Link href={`/collection/v2/${userId}`}>collection feature</Link> may be better suited to your needs for
          tracking this many cards.
        </p>
      )}

      <PhatButton
        className={styles.loadCards}
        icon="download"
        onClick={handleLoadCards}
        disabled={loading}
        color="blue">
        {loading ? 'Loading cards...' : 'Load cards'}
      </PhatButton>
    </Modal>
  )
}

export default XXLDeckLoaderModal
